/**
 * The pure notation domain model.
 *
 * IMPORTANT: this module (and everything under `src/domain/`) must never import
 * React, Firebase, or MUI. It is the part of the app that has to survive the
 * move to Next.js unchanged, and it is what a server-side PDF renderer will
 * consume. Keeping it framework-free is enforced by review for now, and by a
 * lint rule once the code moves to its own package.
 *
 * Shape of a composition:
 *
 *   Document
 *     └─ sections[]        a labelled block (Sthayi, Antara, …)
 *          └─ rows[]       one avartan / line
 *               └─ vectors[]   ONE PER MATRA (beat) — count comes from the taal
 *                    └─ cells[]    one per TRACK (sub-row)
 *                         └─ Cell = string[]   subcells: several swaras in one beat
 *
 * A "track" can come from two places: file-wide tracks (`FileOptions.subRowNames`)
 * and extra per-row tracks (`Row.subRowNames`). That duality is inherited from
 * the original design and is preserved here deliberately.
 */

/**
 * How a track's contents are rendered and edited. This is the extension seam
 * for future lipis (Paluskar, Karnatic) — add a variant plus a codec.
 *
 * NOTE: this type used to live in `pages/File/Editor/classes/EditorFile.ts`,
 * which meant `interfaces/firebase.ts` imported *upwards* from a deep page
 * module. Moving it here inverts that dependency the right way round.
 */
export type IRowRenderType = "simple" | "qwerty-sargam";

export const CONST_RowRenderTypes: IRowRenderType[] = [
    "simple",
    "qwerty-sargam",
];

/**
 * One track's content within a single matra: the swaras packed into that beat.
 * The domain treats these as opaque strings and never parses them — that is
 * what keeps adding a third lipi cheap. Encoding lives in the track codec.
 */
export type Cell = string[];

/** One matra (beat). One cell per track. */
export interface Vector {
    cells: Cell[];
}

/** One avartan / line. `vectors.length` equals the taal's matra count. */
export interface Row {
    vectors: Vector[];
    options: {
        additionalRowVectorElements: number;
    };
    /** Render type for each *row-specific* extra track. */
    renderMethod: IRowRenderType[];
    /** Names of the *row-specific* extra tracks. */
    subRowNames: string[];
}

// ---------------------------------------------------------------------------
// Tempo
// ---------------------------------------------------------------------------

/**
 * How fast the composition is meant to be read.
 *
 * TWO FIELDS ON PURPOSE. `marking` is the free text a musician actually uses;
 * `bpm` is the number a MIDI export or a playback head needs. Nothing here
 * ever infers one from the other, because no total function exists: "drut" is
 * 240 matras per minute to one gharana and 400 to another, and "madhya" is
 * defined only relative to whatever vilambit preceded it. A parser that
 * guessed would be wrong *silently* — the user would see the word they typed
 * and hear a speed nobody chose. See MIDI-EXPORT.md §3.
 */
export interface Tempo {
    /**
     * MATRAS per minute — NOT western quarter notes, not avartans, not tabla
     * strokes. A 16-matra teentaal avartan at 60 bpm lasts sixteen seconds.
     *
     * This is the most confusable field in the model: a reader who assumes
     * quarter notes is wrong by a factor of the taal's matra count.
     *
     * `null` when the musician wrote a marking but no number. That is an
     * ordinary state, not an error — a consumer falls back to its own default
     * rather than reading `marking`.
     */
    bpm: number | null;
    /**
     * What the musician calls it: "vilambit ektaal", "madhya laya",
     * "ati-vilambit, about 15 bpm". Descriptive only, never parsed. Empty
     * string means unset.
     */
    marking: string;
}

/**
 * Bounds on `bpm`, wide on purpose.
 *
 * Ati-vilambit genuinely runs at 10–20 matras per minute — one matra lasting
 * five seconds is normal in a vilambit khayal — and drut comfortably exceeds
 * 300, so a western intuition about tempo would refuse real music. These are
 * runaway-value guards, not musical opinions.
 *
 * The floor is the load-bearing one: a consumer computes `60 / bpm`, so a
 * stored `0` yields Infinity durations and a negative value yields events that
 * travel backwards in time.
 */
export const MIN_TEMPO_BPM = 10;
export const MAX_TEMPO_BPM = 600;

/** Free text, so it needs a cap the storage layer can rely on. */
export const MAX_TEMPO_MARKING_LENGTH = 120;

/**
 * Coerces anything at all into a well-formed `Tempo`, or `null`.
 *
 * TOTAL BY DESIGN, like `parseSections`. It runs on values read straight out
 * of storage and on values typed into an input, and both can be anything.
 *
 * A `bpm` outside the bounds becomes `null` rather than being clamped: a
 * clamped tempo is a number nobody chose, presented as if they had. Losing the
 * value and keeping the marking is the honest degradation.
 *
 * Returns `null` when neither field carries information, so "unset" has one
 * representation instead of three.
 */
export function normaliseTempo(value: unknown): Tempo | null {
    if (!value || typeof value !== "object") return null;

    const raw = value as Partial<Tempo>;

    const bpm =
        typeof raw.bpm === "number" &&
        Number.isFinite(raw.bpm) &&
        raw.bpm >= MIN_TEMPO_BPM &&
        raw.bpm <= MAX_TEMPO_BPM
            ? // One decimal place. Between 10 and 20 the integers are coarse
              // in relative terms, and a musician matching a recording wants
              // 13.5.
              Math.round(raw.bpm * 10) / 10
            : null;

    const marking =
        typeof raw.marking === "string"
            ? raw.marking.trim().slice(0, MAX_TEMPO_MARKING_LENGTH)
            : "";

    return bpm === null && marking === "" ? null : { bpm, marking };
}

// ---------------------------------------------------------------------------
// Tonic
// ---------------------------------------------------------------------------

/**
 * The twelve chromatic note names, in order. Sharps only — an Indian classical
 * musician reading these wants a pitch to tune the tanpura to, not a key
 * signature, so B♭/A♯ spelling arguments do not arise.
 *
 * CANONICAL HERE, not in `web/src/editor/harmonium.ts` where it started. Three
 * reasons, in order of weight:
 *
 *  - `setScaleTonic` has to validate against this list, and the domain may not
 *    import from `web/`. That is a hard rule, not a preference.
 *  - Once anything computes absolute pitch from the tonic — a MIDI or audio
 *    export, per MIDI-EXPORT.md §5 — it runs server-side, where a module that
 *    reaches for the keyboard's glyph table has no business being loaded.
 *  - Twelve duplicated strings are twelve chances to drift. `harmonium.ts`
 *    re-exports these names instead, so every existing import site is unchanged.
 */
export const TONICS = [
    "C",
    "C#",
    "D",
    "D#",
    "E",
    "F",
    "F#",
    "G",
    "G#",
    "A",
    "A#",
    "B",
] as const;

/** The western note Sa is tuned to. */
export type Tonic = (typeof TONICS)[number];

/**
 * What Sa is taken to be when a document does not say.
 *
 * Must stay "C". Every document written before `FileOptions.scaleTonic` existed
 * lacks the field, and the editor has always labelled from C — changing this
 * constant would retune every stored composition at once.
 */
export const DEFAULT_TONIC: Tonic = "C";

/**
 * Runtime guard for a value that is only a `Tonic` by claim.
 *
 * Everything that reaches this comes from outside the type system: a keypress,
 * a stored document, an HTTP body. An unrecognised name rotates the note-name
 * table off its end and mislabels every key on the keyboard.
 */
export const isTonic = (value: unknown): value is Tonic =>
    typeof value === "string" && (TONICS as readonly string[]).includes(value);

/** A labelled block of rows, e.g. Sthayi or Antara. */
export interface Section {
    rows: Row[];
    name: string;
    /**
     * GROUNDWORK — the shape exists and round-trips, but nothing reads it yet.
     *
     * Tempo from this section's first matra onward. Absent or `null` means
     * inherit the FILE tempo, never the previous section's: inheriting from a
     * predecessor would make a section's meaning depend on document order, so
     * `moveSectionUp` / `moveSectionDown` would silently change the tempo of a
     * section nobody touched. File default plus explicit override is
     * order-independent by construction.
     *
     * An object rather than a bare number so it can grow a ramp or a curve
     * later without touching a single call site. That is the whole reason this
     * field is here before there is any UI for it.
     */
    tempo?: Tempo | null;
}

export interface FileRowOptions {
    /** Number of file-wide tracks (sub-rows) each vector carries. */
    vectorLength: number;
}

export interface FileOptions {
    rows: FileRowOptions;
    /** File-wide track names. */
    subRowNames: string[];
    /** File-wide track render types, parallel to `subRowNames`. */
    subRowRenderTypes: IRowRenderType[];
    /** Which track feeds the plain-text summary header; null if unset. */
    summarySubRow: string | null;
    /**
     * How fast the whole composition is meant to be read. Absent on every
     * document written before this field existed; `null` once explicitly
     * cleared. Both mean the same thing.
     *
     * WHY HERE AND NOT ON THE DOCUMENT ROOT beside `name` and `description`:
     * the editor's save path writes `sections` and `fileOptions` and nothing
     * else — deliberately, because re-sending document-root fields is what
     * previously let a stale `updateOn` through and broke "last modified" for
     * the life of the app. A tempo on the root would need a second write path
     * to edit it. Here it round-trips for free, and a missing key inside
     * `fileOptions` is already an ordinary case rather than a migration.
     *
     * The name is a mild abuse — `FileOptions` is otherwise about tracks — and
     * the accepted answer if more performance properties arrive (a persisted
     * tonic is the obvious next one) is to nest them under a `performance`
     * bag. That move is a read-side default and costs nearly nothing; moving
     * the field OUT of `fileOptions` would cost a backfill. The container is
     * the decision worth getting right now.
     */
    tempo?: Tempo | null;
    /**
     * The western note Sa is tuned to. Absent on every document written before
     * this field existed; `null` once explicitly cleared. Both mean
     * `DEFAULT_TONIC`, so behaviour when it is missing is exactly what it was.
     *
     * WHY IT IS PERSISTED. It lived only in the editor's Redux slice, so it
     * reset to C on every reload. That was tolerable while it did nothing but
     * label keyboard keys. It stops being tolerable the moment anything reads
     * it for meaning: Sa is a MOVABLE tonic, so an export that assumes C
     * silently transposes the whole composition (MIDI-EXPORT.md §5 lists this
     * as a real defect an export would otherwise inherit).
     *
     * Typed as `Tonic`, not `string`, so a caller cannot put "H" here without
     * the compiler objecting — and `setScaleTonic` refuses it at runtime too,
     * because the values that actually threaten this field arrive from a stored
     * document or an HTTP body, where no compiler was present.
     *
     * Beside `tempo` for the reason `tempo` gives above: the editor's save path
     * writes `sections` and `fileOptions` and nothing else, so a field in this
     * bag round-trips for free, while one on the document root would need a
     * second write path to edit it.
     */
    scaleTonic?: Tonic | null;
}

/**
 * The parts of a document the editor actually owns and mutates.
 *
 * Every operation is generic over `T extends NotationContent` rather than taking
 * this type directly, so callers can pass their richer document (one carrying
 * Firestore metadata such as `id` and `owner`) and get the same type back with
 * those extra fields preserved. That is how the domain stays Firebase-free
 * while the app still works with full documents.
 */
export interface NotationContent {
    sections: Section[];
    fileOptions: FileOptions;
}

// ---------------------------------------------------------------------------
// Persisted ("outbound") shapes
// ---------------------------------------------------------------------------

/**
 * Firestore cannot store nested arrays, so on the way out each Cell (string[])
 * is joined into a single comma-separated string, and split again on the way
 * in. See `serialization.ts`.
 */
export type CellOutbound = string;

export interface VectorOutbound {
    cells: CellOutbound[];
}

export interface RowOutbound extends Omit<Row, "vectors"> {
    vectors: VectorOutbound[];
}

export interface SectionOutbound extends Omit<Section, "rows"> {
    rows: RowOutbound[];
}
